/**
 * Actycrea: scrollcta.esm.js (ES6)
 * MIT
 *
 * Generic "scroll to next section" control: smooth-scrolls to a target
 * selector on click and drives a subtle bounce loop on its icon while idle.
 * Reusable by any module needing a "read more / scroll down" button —
 * `module-video`'s "Ver más" is the first consumer.
 */
import { gsap } from 'gsap';
import { PREFIX } from '../helpers/config.js';

const ATTR_ROOT     = `data-${PREFIX}-scroll-cta`;
const ATTR_TARGET   = `data-${PREFIX}-scroll-target`;
const SELECTOR_DATA = `[${ATTR_ROOT}]`;
const ICON_SELECTOR = '.scroll-cta-icon';

const DEFAULTS = {
  bounceDistance: 6 ,   // px
  bounceDuration: 0.6   // s
};

export class ScrollCta {
  /**
   * @param {HTMLElement} element
   * @param {Object}      config
   */
  constructor( element , config = { } ){
    if( !(element instanceof HTMLElement) ) throw new Error('ScrollCta: element inválido');

    this._config  = { ...DEFAULTS , ...config };
    this._element = element;

    // bind
    this._onClick = this._onClick.bind( this );

    this._element.addEventListener( 'click' , this._onClick );
    this._bounce( );
  }

  /**
   * Smooth-scrolls to the element matching the button's target selector.
   *
   * @returns {void}
   */
  _onClick( ){
    const targetSelector = this._element.getAttribute( ATTR_TARGET );
    const target = targetSelector ? document.querySelector( targetSelector ) : null;
    if( !target ) return;

    target.scrollIntoView( { behavior: 'smooth' , block: 'start' } );
  }

  /**
   * Loops a subtle vertical bounce on the button's icon.
   *
   * @returns {void}
   */
  _bounce( ){
    const icon = this._element.querySelector( ICON_SELECTOR );
    if( !icon ) return;

    gsap.to( icon , {
      y: this._config.bounceDistance ,
      duration: this._config.bounceDuration ,
      ease: 'sine.inOut' ,
      repeat: -1 ,
      yoyo: true
    } );
  }

  /**
   * Removes the click listener and kills the bounce tween.
   *
   * @returns {void}
   */
  destroy( ){
    this._element.removeEventListener( 'click' , this._onClick );
    const icon = this._element.querySelector( ICON_SELECTOR );
    if( icon ) gsap.killTweensOf( icon );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_DATA );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._scrollCtaInstance ) return;
      el._scrollCtaInstance = new ScrollCta( el );
      instances.push( el._scrollCtaInstance );
    } );
    return instances;
  }
}

export default ScrollCta;
